import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin, of } from "rxjs";
import { toOrganizationTree } from "src/app/_helpers/org-tree-helper";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { AgentOrgLevel } from "src/app/models/agentOrglevel.model";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { AgentOrgLevelService } from "src/app/services/agent-org-level.service";
import { AgentService } from "src/app/services/agent.service";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import {
    FormComponent,
    IFormDialogData,
    IOrganizationLevelEntity,
} from "src/app/shared/form/form.component";
import {
    HierarchicalSelectionFormField,
    SingleSelectionFormField,
    TextAreaFormField,
} from "src/app/shared/form/formField.model";
import { ILookup } from "src/app/shared/form/lookup.model";
import nameOf from "src/app/shared/form/nameOf.helper";

export interface IAgentSiteDialogData
    extends IFormDialogData, IOrganizationLevelEntity {
    agentId: string;
}

@Component({
  standalone: false,
    selector: "app-agent-site-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./../../shared/form/form.component.scss"],
})
export class AgentSiteDialogComponent
    extends FormComponent<AgentOrgLevel>
    implements OnInit
{
    organizationLevels: OrganizationLevel[];
    agents: ILookup[];

    constructor(
        dialogRef: MatDialogRef<AgentSiteDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IAgentSiteDialogData,
        protected agentOrgLevelService: AgentOrgLevelService,
        protected agentService: AgentService,
        private organizationLevelService: OrganizationLevelService,
        protected injector: Injector,
    ) {
        super(
            dialogRef,
            agentOrgLevelService as any,
            data,
            "Agent to Site",
            injector,
        );
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            entity: this.getEntity(),
            organizationLevels: this.organizationLevelService.getAll(),
            agents: this.agentService.getAll(),
        }).subscribe(
            (result) => {
                this.entity = {
                    ...result.entity,
                    agentId: this.data.agentId,
                    organizationLevelId: this.data.organizationLevelId,
                    healthAdvisory: result.entity.healthAdvisory || "",
                    specificRecommendations:
                        result.entity.specificRecommendations || "",
                };
                this.organizationLevels = result.organizationLevels;
                this.agents = result.agents;
                this.initFields();
                this.isFormLoading = false;
            },
            (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        );
    }

    getNewObject(): AgentOrgLevel {
        return new AgentOrgLevel();
    }

    validate(): boolean {
        return true;
    }

    initFields(): void {
        this.fields = [
            new SingleSelectionFormField({
                property: nameOf<AgentOrgLevel>((x) => x.agentId),
                options: this.agents,
                name: "Agent Name",
                columnSpan: new ColumnSpan(1, 2),
                readonly: true,
            }),
            new HierarchicalSelectionFormField({
                property: nameOf<AgentOrgLevel>((x) => x.organizationLevelId),
                options: toOrganizationTree(this.organizationLevels),
                name: "Organization Level",
                columnSpan: new ColumnSpan(1, 2),
                required: true,
            }),
            new TextAreaFormField({
                property: nameOf<AgentOrgLevel>((x) => x.healthAdvisory),
                name: "Health Advisory",
                columnSpan: new ColumnSpan(1, 1),
                required: false,
            }),
            new TextAreaFormField({
                property: nameOf<AgentOrgLevel>(
                    (x) => x.specificRecommendations,
                ),
                name: "Specific Recommendations",
                columnSpan: new ColumnSpan(1, 1),
                required: false,
            }),
        ];
    }
}
